/**
 * Generated by orval v7.9.0 🍺
 * Do not edit manually.
 * Meeting BaaS API
 * API for managing meeting bots, calendar integrations, and webhooks
 * OpenAPI spec version: 2.0.0
 */

import type { BodyType } from "../../../../custom-axios"

import { customInstance } from "../../../../custom-axios"
import type {
  CreateMeetLogin201,
  CreateMeetLoginBody,
  DeleteMeetLogin200,
  GetMeetLogin200,
  GetMeetLoginUtilization200,
  ListMeetLogins200,
  ListMeetLoginsParams,
  UpdateMeetLogin200,
  UpdateMeetLoginBody
} from "../../schema"

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1]

/**
 * Create a meet login — a Google Workspace user identity attached to a parent meet workspace.

    Each login represents one Workspace user that bots can sign in as. Many logins can share one workspace; they all use the workspace's cert/key for SAML signing. Round-robin assignment picks the least-loaded active login when a bot is dispatched with `meet_config.email_group` (or `credential_id`) on `POST /v2/bots`.

    **Prerequisites:** Create a meet workspace first via `POST /v2/meet-workspaces`. Pass the returned `workspace_id` here.

    **Domain Validation:** The `email` must belong to the parent workspace's domain (or a subdomain). For example, if the workspace domain is `bots.acme.com`, valid emails include `bot1@bots.acme.com` or `bot2@dev.bots.acme.com`, but not `bot1@acme.com`. The same rule applies to `email_group` if provided.

    **Email Group (optional but encouraged):** Set `email_group` to a Google Group address that contains the bot users as members. When you put this group address on a calendar invite, the assigned bot lands in Meet's verified queue and bypasses the waiting room. Logins sharing the same `email_group` form one round-robin pool.

    **Per-team Uniqueness on Email:** Each `email` may exist at most once per team. Attempting to register the same email twice returns 409.

    **Concurrency:** Each login supports up to 20 concurrent SSO sessions by default (configurable per environment). When a login hits its capacity, the round-robin assigner skips it and tries the next one in the pool. If all logins in a pool are saturated, the bot creation fails with `MEET_LOGIN_UNAVAILABLE` (or falls back to anonymous, depending on your `fallback` setting).

    **Error Scenarios:**
    - `404 Not Found`: `workspace_id` is unknown or does not belong to your team.
    - `409 Conflict`: A login for this `email` already exists.
    - `422 Unprocessable Entity`: `email` or `email_group` domain does not match the parent workspace's domain.
 * @summary Create a meet login
 */
export const createMeetLogin = (
  createMeetLoginBody: BodyType<CreateMeetLoginBody>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<CreateMeetLogin201>(
    {
      url: "/v2/meet-logins",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: createMeetLoginBody
    },
    options
  )
}
/**
 * List all meet logins for your team across every workspace.

    Each row includes its parent `workspace_id` so you can correlate logins to their workspace without re-fetching. Sensitive fields (cert, key) live on the workspace, not the login, and are never returned through this endpoint.

    **State Field:** Logins can independently be `active` or `invalid`. A login flipping to `invalid` is rare in v1 — most SSO failures are workspace-scoped (cert mismatch) and disable the parent workspace instead. If a specific login is misbehaving (e.g., the Workspace user got suspended), you can manually delete and re-create it.

    **Filtering by workspace:** Not currently supported as a query param. Filter client-side using the `workspace_id` field on each row, or fetch the workspace and use `GET /v2/meet-workspaces/:id` for inventory.

    **Round-Robin Tracking:** `active_session_count` (current concurrent bots using this login) and `last_used_at` (last assignment timestamp) reflect the round-robin scheduler's view. Use these to sanity-check load distribution.
 * @summary List meet logins
 */
export const listMeetLogins = (
  params?: ListMeetLoginsParams,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<ListMeetLogins200>(
    { url: "/v2/meet-logins", method: "GET", params },
    options
  )
}
/**
 * Get current concurrency utilization for your team's meet login pool.

    Returns aggregate metrics across all your active logins:

    - `logins_total`: total number of logins for your team
    - `logins_active`: number with `state: "active"`
    - `logins_invalid`: number with `state: "invalid"`
    - `concurrent_sessions`: current sum of `active_session_count` across active logins (i.e., bots in flight using your auth pool)
    - `concurrent_capacity`: `logins_active × per-login-capacity` (defaults to 20 per login)
    - `utilization_pct`: `concurrent_sessions / concurrent_capacity` as a percentage
    - `by_email_group`: per-pool breakdown of the same metrics

    **Use this with alert rules.** Configure a `meet_login_utilization` threshold alert at e.g. 70% to get notified before saturation, and configure `meet_login_unavailable` operational alerts to know when bots have actually hit the ceiling.

    **Polling:** Cheap to call. Numbers reflect live counters — no caching.
 * @summary Get current login pool utilization
 */
export const getMeetLoginUtilization = (options?: SecondParameter<typeof customInstance>) => {
  return customInstance<GetMeetLoginUtilization200>(
    { url: "/v2/meet-logins/utilization", method: "GET" },
    options
  )
}
/**
 * Get full details for a single meet login.

    Returns the same fields as the list endpoint, scoped to one login.

    Returns 404 if the login is not found or does not belong to your team.
 * @summary Get a meet login
 */
export const getMeetLogin = (
  credentialId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<GetMeetLogin200>(
    { url: `/v2/meet-logins/${credentialId}`, method: "GET" },
    options
  )
}
/**
 * Update a meet login — rename it, change its email_group, or re-enable it after auto-disable.

    **Rename:** pass only `name`.

    **Update email_group:** pass `email_group` to change the round-robin pool the login belongs to. Pass an empty string to clear it (the login becomes pool-less and is only assignable via explicit `credential_id`). The new `email_group` value must satisfy the same domain rule as create — its domain must match (or be a subdomain of) the parent workspace's domain.

    **Re-enable after auto-disable:** pass `state: "active"`. Only valid if the login is currently `invalid`. Clears `failure_data`, `last_error_message`, and `last_error_at`.

    `email` and `workspace_id` are immutable. To change either, delete the login and create a new one.

    **State Restrictions:** PATCH cannot set `state` to `invalid` — that's system-only.

    **Error Scenarios:**
    - `400 Bad Request`: Attempted to set `state` to a value other than `active`.
    - `404 Not Found`: Login not found or does not belong to your team.
    - `422 Unprocessable Entity`: `email_group` domain does not match the parent workspace's domain.
 * @summary Update a meet login
 */
export const updateMeetLogin = (
  credentialId: string,
  updateMeetLoginBody: BodyType<UpdateMeetLoginBody>,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<UpdateMeetLogin200>(
    {
      url: `/v2/meet-logins/${credentialId}`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: updateMeetLoginBody
    },
    options
  )
}
/**
 * Delete a meet login.

    **In-Use Guard:** If the login has any active SSO sessions in flight (`active_session_count > 0`), the request returns 409. Wait for the bots to finish or stop them first.

    **Bot Reference Handling:** Bots that previously used this login keep their reference in `assigned_meet_login_id` until the row is deleted, then the FK is set to `null` (no historical bot record is removed; just the link).

    Returns 404 if the login is not found or does not belong to your team.
 * @summary Delete a meet login
 */
export const deleteMeetLogin = (
  credentialId: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<DeleteMeetLogin200>(
    { url: `/v2/meet-logins/${credentialId}`, method: "DELETE" },
    options
  )
}
export type CreateMeetLoginResult = NonNullable<Awaited<ReturnType<typeof createMeetLogin>>>
export type ListMeetLoginsResult = NonNullable<Awaited<ReturnType<typeof listMeetLogins>>>
export type GetMeetLoginUtilizationResult = NonNullable<
  Awaited<ReturnType<typeof getMeetLoginUtilization>>
>
export type GetMeetLoginResult = NonNullable<Awaited<ReturnType<typeof getMeetLogin>>>
export type UpdateMeetLoginResult = NonNullable<Awaited<ReturnType<typeof updateMeetLogin>>>
export type DeleteMeetLoginResult = NonNullable<Awaited<ReturnType<typeof deleteMeetLogin>>>
